import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { format, parseISO } from "date-fns";

interface DealTask {
  id: string;
  dealId: string;
  dealName: string;
  title: string;
  status: string;
  priority: string;
  assignedTo: string | null;
  assignedToName: string | null;
  startDate: string | null;
  dueDate: string | null;
  isOverdue: boolean;
}

interface DealTasksResponse {
  tasks: DealTask[];
  total: number;
  limit: number;
  offset: number;
}

interface Deal {
  id: string;
  dealName: string;
}

const statusOptions = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const priorityOptions = [
  { value: "all", label: "All Priorities" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

function statusBadgeVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed": return "default";
    case "in_progress": return "secondary";
    case "pending": return "outline";
    default: return "outline";
  }
}

function priorityBadgeVariant(priority: string): "default" | "secondary" | "destructive" | "outline" {
  switch (priority) {
    case "urgent": return "destructive";
    case "high": return "default";
    case "medium": return "secondary";
    case "low": return "outline";
    default: return "outline";
  }
}

export default function DealTasksDashboard() {
  const [, navigate] = useLocation();

  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dealFilter, setDealFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Build query params
  const buildQueryParams = () => {
    const params: Record<string, string> = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (priorityFilter !== "all") params.priority = priorityFilter;
    if (dealFilter !== "all") params.dealId = dealFilter;
    if (overdueOnly) params.overdue = "true";
    return params;
  };

  const queryParams = buildQueryParams();

  const { data, isLoading } = useQuery<DealTasksResponse>({
    queryKey: ["/api/crm/deal-tasks", queryParams],
  });

  // Fetch deals list for the deal filter dropdown
  const { data: deals } = useQuery<Deal[]>({
    queryKey: ["/api/crm/deals"],
  });

  const tasks = data?.tasks || [];

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Deal Tasks"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Deal Tasks" }]}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          <h1 data-testid="page-title" className="text-2xl font-bold">Deal Tasks</h1>
          {/* Filters */}
          <Card className="p-3">
            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-[160px]" data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={priorityFilter}
                onValueChange={setPriorityFilter}
              >
                <SelectTrigger className="w-[160px]" data-testid="filter-priority">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={dealFilter}
                onValueChange={setDealFilter}
              >
                <SelectTrigger className="w-[200px]" data-testid="filter-deal">
                  <SelectValue placeholder="All Deals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Deals</SelectItem>
                  {deals?.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.dealName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2" data-testid="filter-overdue">
                <Checkbox
                  id="overdue-toggle"
                  checked={overdueOnly}
                  onCheckedChange={(checked) => setOverdueOnly(checked === true)}
                />
                <label htmlFor="overdue-toggle" className="text-sm cursor-pointer">
                  Overdue Only
                </label>
              </div>
            </div>
          </Card>

          {/* Tasks Table */}
          <Card>
            <Table data-testid="deal-tasks-table">
              <TableHeader>
                <TableRow className="h-10">
                  <TableHead className="px-2 py-1 text-xs font-medium">Task</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Deal</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Status</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Priority</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Assigned To</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Due Date</TableHead>
                  <TableHead className="px-2 py-1 text-xs font-medium">Overdue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="px-2 py-8 text-center text-muted-foreground">
                      Loading deal tasks...
                    </TableCell>
                  </TableRow>
                ) : tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="px-2 py-8 text-center text-muted-foreground">
                      No deal tasks found. Try adjusting your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  tasks.map((task) => (
                    <TableRow key={task.id} className="h-10 hover:bg-muted/50">
                      <TableCell className="px-2 py-1 text-sm font-medium">
                        {task.title}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm">
                        <a
                          href={`/deals/${task.dealId}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/deals/${task.dealId}`);
                          }}
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {task.dealName}
                        </a>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge variant={statusBadgeVariant(task.status)} className="text-xs capitalize">
                          {task.status.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        <Badge variant={priorityBadgeVariant(task.priority)} className="text-xs capitalize">
                          {task.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                        {task.assignedToName || "-"}
                      </TableCell>
                      <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                        {task.dueDate ? format(parseISO(task.dueDate), "MMM d, yyyy") : "-"}
                      </TableCell>
                      <TableCell className="px-2 py-1">
                        {task.isOverdue ? (
                          <Badge variant="destructive" className="text-xs">Overdue</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Footer info */}
          {data && (
            <div className="text-sm text-muted-foreground">
              Showing {tasks.length} of {data.total} task{data.total !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
